<template>
  <v-dialog v-model="dialog" max-width="1400px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ $t('tslOrders.previewOrder') }}</span>
        <div>
          <v-btn
            v-if="orderId"
            color="primary"
            outlined
            class="mr-2"
            :loading="downloading"
            @click="downloadPDF"
          >
            <v-icon left>mdi-download</v-icon>
            {{ $t('tslOrders.downloadPDF') }}
          </v-btn>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text class="pa-0 overflow-hidden">
        <v-progress-linear v-if="loading" indeterminate />
        <PreviewTab
          v-if="!loading && formData"
          :form-data="formData"
          :client-contractor="clientContractor"
          :carrier-contractor="carrierContractor"
          :is-edit-mode="true"
          :order-number="orderNumber"
          :order-id="orderId"
          :hide-actions="true"
          :is-active="true"
          @save="handleSave"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Service from '@/services'
import PreviewTab from '../tabs/PreviewTab'

export default {
  name: 'OrderPreviewDialog',
  components: {
    PreviewTab
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      downloading: false,
      formData: null,
      clientContractor: null,
      carrierContractor: null,
      orderNumber: null
    }
  },
  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    dialog (val) {
      if (val && this.orderId) {
        this.loadOrderDetails()
      }
    }
  },
  methods: {
    async loadOrderDetails () {
      this.loading = true
      try {
        const { data } = await Service.get('/TSLOrder/GetOrderDetails', { id: this.orderId })
        this.orderNumber = data.orderNumber
        this.formData = {
          clientContractorId: data.clientContractorId,
          carrierContractorId: data.carrierContractorId,
          places: data.places || [],
          notes: data.notes || '',
          cargoDescription: data.cargoDescription || '',
          ftlLtl: data.ftlLtl || [],
          customsInfo: data.customsInfo || '',
          cabotageInfo: data.cabotageInfo || '',
          adrInfo: data.adrInfo || '',
          clientPhone: data.clientPhone || '',
          clientEmail: data.clientEmail || '',
          clientPrice: data.clientPrice || null,
          clientCurrency: data.clientCurrency ?? 0,
          clientRefNumber: data.clientRefNumber || '',
          carrierFullName: data.carrierFullName || '',
          carrierPhone: data.carrierPhone || '',
          carrierEmail: data.carrierEmail || '',
          carrierPrice: data.carrierPrice || null,
          carrierCurrency: data.carrierCurrency ?? 0,
          carrierVehicleType: data.carrierVehicleType || '',
          carrierNotes: data.carrierNotes || '',
          carrierRegistrationNumber: data.carrierRegistrationNumber || '',
          carrierPaymentType: data.carrierPaymentType || null,
          carrierPaymentDays: data.carrierPaymentDays || null,
          createdAt: data.createdAt
        }
        await this.loadContractorDetails(data.clientContractorId, data.carrierContractorId)
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error loading order')
      } finally {
        this.loading = false
      }
    },
    async loadContractorDetails (clientContractorId, carrierContractorId) {
      try {
        // Load client contractor details
        if (clientContractorId) {
          const { data } = await Service.get('/TSLContractor/GetContractorDetails', { id: clientContractorId })
          this.clientContractor = {
            id: data.id,
            companyName: data.companyName,
            vatNumber: data.vatNumber,
            street: data.street,
            zipCode: data.zipCode,
            city: data.city,
            country: data.country
          }
        }

        // Load carrier contractor details
        if (carrierContractorId) {
          const { data } = await Service.get('/TSLContractor/GetContractorDetails', { id: carrierContractorId })
          this.carrierContractor = {
            id: data.id,
            companyName: data.companyName,
            vatNumber: data.vatNumber,
            street: data.street,
            zipCode: data.zipCode,
            city: data.city,
            country: data.country
          }
        }
      } catch (e) {
        console.error('Error loading contractor details:', e)
      }
    },
    async downloadPDF () {
      if (!this.orderId) {
        await this.$store.dispatch('setErrorNotification', this.$t('tslOrders.messages.orderNotSaved'))
        return
      }

      try {
        this.downloading = true
        const response = await Service.getFile('TSLOrder/DownloadOrderPDF', {
          orderId: this.orderId
        })

        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Zlecenie_${this.orderNumber.replace(/\//g, '-')}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        await this.$store.dispatch('setErrorNotification', this.$t('tslOrders.messages.downloadError'))
      } finally {
        this.downloading = false
      }
    },
    handleSave () {
      // This is a read-only preview, so we don't handle save
      // The PreviewTab component might emit this, but we ignore it
    },
    closeDialog () {
      this.$emit('close')
      this.dialog = false
    }
  }
}
</script>
